import { listUsers } from '@/backend/listUsers'
import { removeMember } from '@/backend/removeMember'
import { updateWorkspaceMember } from '@/backend/updateWorkspaceMember'
import { buildResourceClient } from '@/data/ResourceMembers/buildResourceMemberClient'
import type { ResourceMemberRegistryItem } from '../types'

export const workspaceRegistryItem: ResourceMemberRegistryItem<'workspace'> = {
  useResourceMembers: buildResourceClient({
    loadMembers: async ({ workspaceId }) => listUsers(workspaceId),
    addResourceMember: async () => {
      /**
       * We need this to satisfy typescript, but in reality we will never call this
       * function as we can't directly add a workspace member, instead we send
       * an invitation and the user is added to the workspace once they accept.
       */
      return {
        ok: false,
        error: {
          code: 'Not supported for workspaces',
          message: 'Workspace members are invited, not added.',
        },
      }
    },
    updateResourceMember: async ({ workspaceId, userId, role }) =>
      updateWorkspaceMember({
        role,
        userId,
        workspaceId,
      }),
    removeResourceMember: async ({ workspaceId, userId }) =>
      removeMember({
        workspaceId,
        userId,
      }),
    updateAccessType: async () => {
      /**
       * We need this to satisfy typescript, but in reality we will never call this
       * function as workspaces don't have access types - either you're a member of the
       * workspace or you're not.
       *
       * There was talk about 'public' workspaces that are visible to people without
       * an account, but this hasn't been implemented yet.
       */
      return {
        ok: false,
        error: {
          code: 'Not supported for workspaces',
          message: 'Workspaces are always private.',
        },
      }
    },
  }),
  text: {
    defaultAccess: null,
    inviteUsers: {
      roles: {
        admin: {
          title: 'Admin',
          description: 'Can create and delete all resources, and manage user access for everyone.',
        },
        editor: {
          title: 'Editor',
          description: 'Can create all resources, and can invite other users to the workspace.',
        },
        reviewer: {
          title: 'Reviewer',
          description:
            'Cannot create or configure agents, but they can review agent outputs and use cases.',
        },
        worker: {
          title: 'Worker',
          description: 'Can view only agents where they have assigned work.',
        },
        reader: {
          title: 'Reader',
          description:
            'Cannot update any data, can only view resources. Cannot create or view cases.',
        },
      },
    },
  },
}
